import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Divider,
  Grid,
  LinearProgress,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DockerIcon from '@mui/icons-material/Storage';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import { generateSystemReport } from './api';

const SystemReportPanel = ({ clientDetails, onError, onRefresh, apiKey }) => {
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [showRefreshPrompt, setShowRefreshPrompt] = useState(false);
  const timeoutRef = useRef(null);
  const waitingForUpdateRef = useRef(false);
  const lastSeenRef = useRef(null);

  // Load report from clientDetails when available
  useEffect(() => {
    if (clientDetails?.systemInfo?.report) {
      const newReport = clientDetails.systemInfo.report;
      const currentLastSeen = clientDetails.lastSeen;
      
      // If we're waiting for new data and lastSeen changed, stop loading
      if (waitingForUpdateRef.current && lastSeenRef.current && currentLastSeen && lastSeenRef.current !== currentLastSeen) {
        setGenerating(false);
        setShowRefreshPrompt(false);
        setError('');
        
        // Clear timeout since data arrived
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        waitingForUpdateRef.current = false;
      }
      
      setReport(newReport);
      lastSeenRef.current = currentLastSeen;
    }
  }, [clientDetails, generating]);

  const generateReport = async () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setGenerating(true);
    setShowRefreshPrompt(false);
    setError('');
    
    // Capture current lastSeen before generation
    lastSeenRef.current = clientDetails?.lastSeen;
    waitingForUpdateRef.current = true;
    
    try {
      // Call API to trigger report generation on agent
      const response = await generateSystemReport(apiKey, clientDetails.id);
      
      // Poll for updates: check every 3 seconds for up to 30 seconds
      let pollCount = 0;
      const maxPolls = 10; // 10 polls x 3 seconds = 30 seconds
      
      const pollInterval = setInterval(async () => {
        pollCount++;
        
        // Refresh client details from backend
        if (onRefresh) {
          await onRefresh();
        }
        
        // If we've hit max polls or data arrived (waitingForUpdateRef will be false), stop
        if (pollCount >= maxPolls || !waitingForUpdateRef.current) {
          clearInterval(pollInterval);
          
          if (waitingForUpdateRef.current) {
            // Still waiting after max polls - show refresh prompt
            setGenerating(false);
            setShowRefreshPrompt(true);
            setError('Report generation is taking longer than expected. Please click "Refresh View" to check if the report is ready.');
            waitingForUpdateRef.current = false;
          }
        }
      }, 3000); // Poll every 3 seconds
      
      // Store interval ID for cleanup
      timeoutRef.current = pollInterval;
      
    } catch (err) {
      console.error('Failed to generate report:', err);
      setError(err.message || 'Failed to trigger report generation. Client may be offline.');
      setGenerating(false);
      setShowRefreshPrompt(false);
      waitingForUpdateRef.current = false;
      
      // Clear timeout on error
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      if (onError) {
        onError(err.message || 'Failed to generate report');
      }
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
    };
  }, []);

  const refreshReport = async () => {
    setGenerating(true);
    setShowRefreshPrompt(false);
    setError('');
    
    try {
      // Force refresh from backend
      if (onRefresh) {
        await onRefresh();
      }
      setGenerating(false);
    } catch (err) {
      console.error('Failed to refresh:', err);
      setError('Failed to refresh data');
      setGenerating(false);
    }
  };

  const getStatusIcon = (software) => {
    if (!software) return <WarningIcon color="warning" />;
    if (!software.installed) return <ErrorIcon color="error" />;
    if (software.meetsRequirement) return <CheckCircleIcon color="success" />;
    return <WarningIcon color="warning" />;
  };

  const getStatusChip = (software, label) => {
    if (!software) {
      return <Chip label="N/A" size="small" color="default" />;
    }
    if (!software.installed) {
      return <Chip label="Not Installed" size="small" color="error" icon={<ErrorIcon />} />;
    }
    if (software.meetsRequirement) {
      return <Chip label="OK" size="small" color="success" icon={<CheckCircleIcon />} />;
    }
    return <Chip label="Version Mismatch" size="small" color="warning" icon={<WarningIcon />} />;
  };

  const getWatchdogChip = (watchdog) => {
    if (!watchdog) {
      return <Chip label="N/A" size="small" color="default" />;
    }
    if (watchdog.isRunning) {
      return <Chip label="Running" size="small" color="success" icon={<CheckCircleIcon />} />;
    }
    return <Chip label="Not Running" size="small" color="error" icon={<ErrorIcon />} />;
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AssessmentIcon />}
          onClick={generateReport}
          disabled={generating || !clientDetails?.id}
          title={!clientDetails?.id ? 'Client details not loaded' : 'Request agent to generate fresh system report'}
        >
          {generating ? 'Generating...' : 'Generate Fresh Report'}
        </Button>
        <Button
          variant="outlined"
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={refreshReport}
          disabled={generating}
          title="Reload latest report data from backend"
          color={showRefreshPrompt ? "warning" : "primary"}
        >
          {generating ? 'Refreshing...' : 'Refresh View'}
        </Button>
        {generating && (
          <Typography variant="body2" color="primary">
            Waiting for new report data...
          </Typography>
        )}
        {showRefreshPrompt && !generating && (
          <Alert severity="info" sx={{ flex: 1 }}>
            Report generation may be complete. Click "Refresh View" to check for new data.
          </Alert>
        )}
        {report && !generating && !showRefreshPrompt && report.timestamp && (
          <Typography variant="body2" color="text.secondary">
            Last generated: {new Date(report.timestamp).toLocaleString()}
          </Typography>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {generating && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack spacing={1}>
            <Typography variant="body2">
              Report generation in progress. The agent is collecting system information...
            </Typography>
            <LinearProgress />
          </Stack>
        </Alert>
      )}

      {report && (
        <Grid container spacing={2}>
          {/* Docker Check - Combined with WoConnectCtl Docker Info */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <DockerIcon color="primary" />
                  <Typography variant="h6">Docker</Typography>
                  {getStatusIcon(report.docker)}
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell>{getStatusChip(report.docker, 'Docker')}</TableCell>
                    </TableRow>
                    {report.docker?.installed && (
                      <>
                        <TableRow>
                          <TableCell><strong>Current Version</strong></TableCell>
                          <TableCell>{report.docker.version || 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Required Version</strong></TableCell>
                          <TableCell>{report.docker.requiredVersion || 'N/A'}</TableCell>
                        </TableRow>
                      </>
                    )}
                    {report.docker?.error && (
                      <TableRow>
                        <TableCell><strong>Error</strong></TableCell>
                        <TableCell>
                          <Typography variant="body2" color="error">
                            {report.docker.error}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {/* WoConnectCtl Docker Information */}
                    {report.woConnectCtl?.installed && report.woConnectCtl.connectionStatus?.dockerService && (
                      <TableRow>
                        <TableCell><strong>Docker Service</strong></TableCell>
                        <TableCell>
                          <Chip 
                            label={report.woConnectCtl.connectionStatus.dockerService} 
                            size="small" 
                            color={report.woConnectCtl.connectionStatus.dockerService === 'Connected' ? 'success' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    {report.woConnectCtl?.installed && report.woConnectCtl.checkResult?.dockerClient && (
                      <TableRow>
                        <TableCell><strong>Client Version</strong></TableCell>
                        <TableCell>{report.woConnectCtl.checkResult.dockerClient}</TableCell>
                      </TableRow>
                    )}
                    {report.woConnectCtl?.installed && report.woConnectCtl.checkResult?.dockerServer && (
                      <TableRow>
                        <TableCell><strong>Server Version</strong></TableCell>
                        <TableCell>{report.woConnectCtl.checkResult.dockerServer}</TableCell>
                      </TableRow>
                    )}
                    {report.woConnectCtl?.installed && report.woConnectCtl.checkResult && (
                      <TableRow>
                        <TableCell><strong>Docker Bridge</strong></TableCell>
                        <TableCell>
                          <Chip 
                            label={report.woConnectCtl.checkResult.dockerBridge ? 'Working' : 'Not Working'} 
                            size="small" 
                            color={report.woConnectCtl.checkResult.dockerBridge ? 'success' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* FFMPEG Check */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <VideoLibraryIcon color="primary" />
                  <Typography variant="h6">FFMPEG</Typography>
                  {getStatusIcon(report.ffmpeg)}
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell>{getStatusChip(report.ffmpeg, 'FFMPEG')}</TableCell>
                    </TableRow>
                    {report.ffmpeg?.installed && (
                      <>
                        <TableRow>
                          <TableCell><strong>Current Version</strong></TableCell>
                          <TableCell>{report.ffmpeg.version || 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Required Version</strong></TableCell>
                          <TableCell>{report.ffmpeg.requiredVersion || 'N/A'}</TableCell>
                        </TableRow>
                      </>
                    )}
                    {report.ffmpeg?.error && (
                      <TableRow>
                        <TableCell><strong>Error</strong></TableCell>
                        <TableCell>
                          <Typography variant="body2" color="error">
                            {report.ffmpeg.error}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* AI Accelerator / GPU */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <DeveloperBoardIcon color="primary" />
                  <Typography variant="h6">AI Accelerator / GPU</Typography>
                  {report.aiAccelerator && (
                    <Chip 
                      label={`Total: ${report.aiAccelerator.totalCount}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </Stack>
                <Divider sx={{ mb: 2 }} />
                {report.aiAccelerator && report.aiAccelerator.accelerators && report.aiAccelerator.accelerators.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell><strong>Model</strong></TableCell>
                          <TableCell><strong>Count</strong></TableCell>
                          <TableCell><strong>Driver Version</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.aiAccelerator.accelerators.map((acc, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Chip 
                                label={acc.type || 'N/A'} 
                                size="small" 
                                color={acc.type === 'CPU' ? 'default' : 'success'}
                              />
                            </TableCell>
                            <TableCell>{acc.model || acc.name || 'N/A'}</TableCell>
                            <TableCell>{acc.count || 1}</TableCell>
                            <TableCell>
                              {acc.driverVersion || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No AI accelerators detected
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* WMS */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6">WMS (woconnect management system)</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell>
                        <Chip 
                          label={report.wms || 'Unknown'} 
                          size="small" 
                          color={report.wms && report.wms !== 'Unknown' ? 'success' : 'default'} 
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="caption" color="text.secondary">
                          Detected: {report.wms === 'DriveThru' ? 'DT (DriveThru)' : 
                                    report.wms === 'PersonSuite' ? 'PersonSuite' : 
                                    report.wms === 'Wopipe' ? 'Wopipe' : 'None'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Watchdog Check */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <SecurityIcon color="primary" />
                  <Typography variant="h6">Watchdog Service</Typography>
                  {report.watchdog && (
                    report.watchdog.isRunning ? 
                      <CheckCircleIcon color="success" /> : 
                      <ErrorIcon color="error" />
                  )}
                </Stack>
                <Divider sx={{ mb: 2 }} />
                {report.watchdog ? (
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell width="200"><strong>Service Name</strong></TableCell>
                        <TableCell>{report.watchdog.serviceName || 'dt-watchdog'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell>{getWatchdogChip(report.watchdog)}</TableCell>
                      </TableRow>
                      {report.watchdog.status && (
                        <TableRow>
                          <TableCell><strong>Service State</strong></TableCell>
                          <TableCell>{report.watchdog.status}</TableCell>
                        </TableRow>
                      )}
                      {report.watchdog.error && (
                        <TableRow>
                          <TableCell><strong>Error</strong></TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              {report.watchdog.error}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Watchdog information not available
                  </Typography>
                )}
                <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Note:</strong> Watchdog service names are detected from the installed service on the client machine.
                    Watchdog monitors ingestion services and automatically restarts them if not running.
                    It checks the pipeline status API and manages Docker containers (ingestion & triton) and hailort service.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* WoConnectCtl Check */}
          {report.woConnectCtl && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <AssessmentIcon color="primary" />
                    <Typography variant="h6">WoConnect Control (woconnectctl)</Typography>
                    {report.woConnectCtl.installed ? 
                      <CheckCircleIcon color="success" /> : 
                      <ErrorIcon color="error" />
                    }
                  </Stack>
                  <Divider sx={{ mb: 2 }} />

                  {!report.woConnectCtl.installed && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {report.woConnectCtl.error || 'woconnectctl not installed or not accessible'}
                    </Alert>
                  )}

                  {report.woConnectCtl.installed && (
                    <>
                      {/* Connection Status */}
                      {report.woConnectCtl.connectionStatus && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Connection Status
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell width="200"><strong>DB Connection</strong></TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={report.woConnectCtl.connectionStatus.dbConnection || 'Unknown'} 
                                      size="small" 
                                      color={report.woConnectCtl.connectionStatus.dbConnection === 'Connected' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell><strong>WoConnect API</strong></TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={report.woConnectCtl.connectionStatus.woConnectAPI || 'Unknown'} 
                                      size="small" 
                                      color={report.woConnectCtl.connectionStatus.woConnectAPI === 'Connected' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell><strong>Account Linked</strong></TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={report.woConnectCtl.connectionStatus.accountLinked || 'Unknown'} 
                                      size="small" 
                                      color={report.woConnectCtl.connectionStatus.accountLinked === 'Linked' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell><strong>WoConnect MQ</strong></TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={report.woConnectCtl.connectionStatus.woConnectMQ || 'Unknown'} 
                                      size="small" 
                                      color={report.woConnectCtl.connectionStatus.woConnectMQ === 'Connected' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell><strong>WoConnect Socket</strong></TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={report.woConnectCtl.connectionStatus.woConnectSocket || 'Unknown'} 
                                      size="small" 
                                      color={report.woConnectCtl.connectionStatus.woConnectSocket === 'Connected' ? 'success' : 'error'}
                                    />
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}

                      {/* System Status */}
                      {report.woConnectCtl.systemStatus && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            System Status
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                              <TableBody>
                                {report.woConnectCtl.systemStatus.deviceID && (
                                  <TableRow>
                                    <TableCell width="200"><strong>Device ID</strong></TableCell>
                                    <TableCell>{report.woConnectCtl.systemStatus.deviceID}</TableCell>
                                  </TableRow>
                                )}
                                {report.woConnectCtl.systemStatus.serviceVersion && (
                                  <TableRow>
                                    <TableCell><strong>Service Version</strong></TableCell>
                                    <TableCell>{report.woConnectCtl.systemStatus.serviceVersion}</TableCell>
                                  </TableRow>
                                )}
                                {report.woConnectCtl.systemStatus.timezone && (
                                  <TableRow>
                                    <TableCell><strong>Timezone</strong></TableCell>
                                    <TableCell>{report.woConnectCtl.systemStatus.timezone}</TableCell>
                                  </TableRow>
                                )}
                                {report.woConnectCtl.systemStatus.integrationType && (
                                  <TableRow>
                                    <TableCell><strong>Integration Type</strong></TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={report.woConnectCtl.systemStatus.integrationType} 
                                        size="small" 
                                        color="primary"
                                      />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}

                      {/* Check Results */}
                      {report.woConnectCtl.checkResult && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            System Checks
                          </Typography>
                          
                          {/* Status Summary */}
                          {report.woConnectCtl.checkResult.statusSummary && Object.keys(report.woConnectCtl.checkResult.statusSummary).length > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                              {Object.entries(report.woConnectCtl.checkResult.statusSummary).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={key}
                                  size="small"
                                  color={value ? 'success' : 'error'}
                                  icon={value ? <CheckCircleIcon /> : <ErrorIcon />}
                                />
                              ))}
                            </Stack>
                          )}

                          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                              <TableBody>
                                {report.woConnectCtl.checkResult.systemTime && (
                                  <TableRow>
                                    <TableCell width="200"><strong>System Time</strong></TableCell>
                                    <TableCell>{report.woConnectCtl.checkResult.systemTime}</TableCell>
                                  </TableRow>
                                )}
                                {report.woConnectCtl.checkResult.serverTime && (
                                  <TableRow>
                                    <TableCell><strong>Server Time</strong></TableCell>
                                    <TableCell>{report.woConnectCtl.checkResult.serverTime}</TableCell>
                                  </TableRow>
                                )}
                                {report.woConnectCtl.checkResult.timeDifference && (
                                  <TableRow>
                                    <TableCell><strong>Time Difference</strong></TableCell>
                                    <TableCell>{report.woConnectCtl.checkResult.timeDifference}</TableCell>
                                  </TableRow>
                                )}
                                {report.woConnectCtl.checkResult.hailoRTVersion && (
                                  <TableRow>
                                    <TableCell><strong>HailoRT Version</strong></TableCell>
                                    <TableCell>{report.woConnectCtl.checkResult.hailoRTVersion}</TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Whitelist Services */}
                          {report.woConnectCtl.checkResult.whitelistServices && report.woConnectCtl.checkResult.whitelistServices.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Whitelist Connectivity
                              </Typography>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell><strong>Service Name</strong></TableCell>
                                      <TableCell><strong>Hostname</strong></TableCell>
                                      <TableCell><strong>Port</strong></TableCell>
                                      <TableCell><strong>Protocol</strong></TableCell>
                                      <TableCell><strong>Connected</strong></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {report.woConnectCtl.checkResult.whitelistServices.map((service, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>{service.serviceName}</TableCell>
                                        <TableCell>{service.hostname}</TableCell>
                                        <TableCell>{service.port}</TableCell>
                                        <TableCell>
                                          <Chip label={service.protocol} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                          <Chip 
                                            label={service.connected} 
                                            size="small" 
                                            color={service.connected === 'Yes' ? 'success' : 'error'}
                                            icon={service.connected === 'Yes' ? <CheckCircleIcon /> : <ErrorIcon />}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                          {/* Warnings */}
                          {report.woConnectCtl.checkResult.warnings && report.woConnectCtl.checkResult.warnings.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Warnings
                              </Typography>
                              <Alert severity="warning">
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {report.woConnectCtl.checkResult.warnings.map((warning, idx) => (
                                    <li key={idx}>{warning}</li>
                                  ))}
                                </ul>
                              </Alert>
                            </Box>
                          )}
                        </Box>
                      )}

                      {report.woConnectCtl.error && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          {report.woConnectCtl.error}
                        </Alert>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default SystemReportPanel;
